import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  requestNotificationPermission,
  subscribeToPush,
  getNotificationPermission,
  isSubscribed
} from '../utils/pushNotifications';

const NotificationPrompt = () => {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if we should show the prompt
    const checkStatus = async () => {
      const permission = getNotificationPermission();
      const subscribed = await isSubscribed();

      // Show if notifications are supported, not denied, and not subscribed
      if (permission !== 'unsupported' && permission !== 'denied' && !subscribed) {
        // Wait a bit before showing (don't annoy immediately)
        setTimeout(() => setShow(true), 3000);
      }
    };

    checkStatus();
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    try {
      await requestNotificationPermission();
      await subscribeToPush();
      setShow(false);
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      alert('Could not enable notifications. Please check your browser settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    // Don't show again for this session
    sessionStorage.setItem('notification-prompt-dismissed', 'true');
  };

  // Don't show if dismissed in this session
  if (sessionStorage.getItem('notification-prompt-dismissed')) {
    return null;
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 100, damping: 20 }}
          style={{
            position: 'fixed',
            bottom: 'calc(65px + env(safe-area-inset-bottom) + 10px)',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'calc(100% - 40px)',
            maxWidth: '400px',
            zIndex: 1002,
            background: 'linear-gradient(135deg, rgba(0, 123, 255, 0.95), rgba(0, 200, 255, 0.95))',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            border: '3px solid #000',
            padding: '20px',
            boxShadow: '0 10px 40px rgba(0, 123, 255, 0.4)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
            <span style={{ fontSize: '2.5em' }}>🔔</span>
            <div style={{ flex: 1 }}>
              <h3 style={{
                margin: '0 0 8px 0',
                fontFamily: 'var(--font-display)',
                fontSize: '1.1em',
                color: '#fff',
                textShadow: 'var(--text-stroke-effect-body)'
              }}>
                Stay in the loop!
              </h3>
              <p style={{
                margin: '0 0 15px 0',
                fontSize: '0.9em',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: 1.4
              }}>
                Get notified when someone wants to join your events or when your requests are accepted!
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleEnable}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: '12px',
                    fontSize: '0.95em',
                    fontWeight: 'bold',
                    background: '#fff',
                    color: '#007bff',
                    border: '3px solid #000',
                    borderRadius: '12px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1
                  }}
                >
                  {loading ? 'Enabling...' : 'Enable'}
                </button>
                <button
                  onClick={handleDismiss}
                  style={{
                    padding: '12px 20px',
                    fontSize: '0.95em',
                    background: 'rgba(0, 0, 0, 0.3)',
                    color: '#fff',
                    border: '3px solid #000',
                    borderRadius: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Later
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationPrompt;
